import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, Car, ChevronRight, Archive } from 'lucide-react';
import { ListScreen } from './ListScreen';
import { supabase } from '../../../../lib/supabase';
import { useVertical } from '../../../../context/VerticalContext';
import { reportError } from '../../../../lib/errors';

/**
 * Browse everyone / every vehicle.
 *
 * The Clients page could only SEARCH, which meant you had to already know who
 * you were after. That gap is sharper here than in most apps: email and phone
 * cannot be substring-searched by design (they are encrypted, with an exact
 * blind index), so "Dave something, blue Transit" had no path at all. This is
 * that path.
 */
interface CustomerRow {
    id: string;
    full_name: string;
    created_at: string;
    vrm: string | null;
    vehicle_desc: string | null;
    last_seen: string | null;
    archived_at: string | null;
}

interface VehicleRow {
    id: string;
    vrm: string;
    make: string | null;
    model: string | null;
    year: number | null;
    owner_id: string | null;
    owner_name: string | null;
}

const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

export default function BrowsePage() {
    const { kind } = useParams<{ kind: string }>();
    const navigate = useNavigate();
    const { activeOrgId } = useVertical();
    const isVehicles = kind === 'vehicles';

    const [customers, setCustomers] = useState<CustomerRow[]>([]);
    const [showArchived, setShowArchived] = useState(false);
    const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
    // Derived, not stored: the list is "loading" until the kind on screen is
    // the kind that has actually landed. Setting a flag from inside the effect
    // is the same fact held twice, and it renders a frame out of date.
    const [loadedKind, setLoadedKind] = useState<string | null>(null);
    const kindKey = isVehicles ? 'vehicles' : 'customers';
    const loading = loadedKind !== kindKey;

    useEffect(() => {
        if (!activeOrgId) return;
        let cancelled = false;
        void (async () => {

            if (isVehicles) {
                const { data, error } = await supabase
                    .from('vehicles')
                    .select('id, vrm, make, model, year, owner_id, customers:customers!vehicles_owner_id_fkey(full_name)')
                    .eq('org_id', activeOrgId)
                    .order('vrm');
                if (error) reportError(error, 'browse.vehicles');
                if (cancelled) return;
                setVehicles(
                    (data ?? []).map((v) => {
                        const row = v as unknown as VehicleRow & { customers?: { full_name?: string } | null };
                        return {
                            id: row.id,
                            vrm: row.vrm,
                            make: row.make,
                            model: row.model,
                            year: row.year,
                            owner_id: row.owner_id,
                            owner_name: row.customers?.full_name ?? null,
                        };
                    }),
                );
            } else {
                // full_name only — the encrypted columns would read NULL and the
                // list is browsed by name anyway.
                // Archived customers are fetched, not filtered out in the
                // query: the toggle below has to be able to show them without a
                // second round trip, and the count on it has to be truthful.
                const { data, error } = await supabase
                    .from('customers')
                    .select('id, full_name, created_at, archived_at')
                    .eq('org_id', activeOrgId)
                    .order('full_name');
                if (error) reportError(error, 'browse.customers');

                const rows = (data ?? []) as { id: string; full_name: string; created_at: string; archived_at: string | null }[];

                // Their newest vehicle and last visit, in two set-based reads rather
                // than a query per customer.
                const [{ data: vRows, error: vErr }, { data: sRows, error: sErr }] = await Promise.all([
                    supabase
                        .from('vehicles')
                        .select('owner_id, vrm, make, model, created_at')
                        .eq('org_id', activeOrgId)
                        .order('created_at', { ascending: false }),
                    supabase
                        .from('service_history')
                        .select('customer_id, service_date')
                        .eq('org_id', activeOrgId)
                        .order('service_date', { ascending: false }),
                ]);
                if (vErr) reportError(vErr, 'browse.customers-vehicles');
                if (sErr) reportError(sErr, 'browse.customers-service-history');

                const newestVehicle = new Map<string, { vrm: string; desc: string | null }>();
                for (const v of (vRows ?? []) as { owner_id: string | null; vrm: string; make: string | null; model: string | null }[]) {
                    if (!v.owner_id || newestVehicle.has(v.owner_id)) continue;
                    newestVehicle.set(v.owner_id, {
                        vrm: v.vrm,
                        desc: [v.make, v.model].filter(Boolean).join(' ') || null,
                    });
                }
                const lastSeen = new Map<string, string>();
                for (const s of (sRows ?? []) as { customer_id: string | null; service_date: string }[]) {
                    if (!s.customer_id || lastSeen.has(s.customer_id)) continue;
                    lastSeen.set(s.customer_id, s.service_date);
                }

                if (cancelled) return;
                setCustomers(
                    rows.map((c) => ({
                        id: c.id,
                        full_name: c.full_name,
                        created_at: c.created_at,
                        vrm: newestVehicle.get(c.id)?.vrm ?? null,
                        vehicle_desc: newestVehicle.get(c.id)?.desc ?? null,
                        last_seen: lastSeen.get(c.id) ?? null,
                        archived_at: c.archived_at,
                    })),
                );
            }

            if (!cancelled) setLoadedKind(kindKey);
        })();
        // Switching between customers and vehicles mid-fetch must not let the
        // previous list land in the new one.
        return () => { cancelled = true; };
    }, [activeOrgId, isVehicles, kindKey]);

    const plate = (vrm: string) => (
        <span
            className="inline-block px-1.5 py-0.5 rounded-sm font-mono text-[11px] font-bold tracking-wider shrink-0"
            style={{ backgroundColor: 'var(--fd-plate)', color: 'var(--fd-plate-ink)' }}
        >
            {vrm}
        </span>
    );

    if (isVehicles) {
        return (
            <ListScreen<VehicleRow>
                title="All vehicles"
                subtitle={`${vehicles.length} on file`}
                icon={Car}
                items={vehicles}
                loading={loading}
                keyOf={(v) => v.id}
                searchText={(v) => [v.vrm, v.make, v.model, v.owner_name].filter(Boolean).join(' ')}
                searchPlaceholder="Search registration, make or owner…"
                emptyTitle="No vehicles yet"
                emptyBody="Vehicles appear here as you book them in or look up a registration."
                renderItem={(v) => (
                    <button
                        onClick={() => v.owner_id && navigate(`/clients/${v.owner_id}`)}
                        disabled={!v.owner_id}
                        className="w-full text-left rounded-xl border border-line bg-panel p-4 hover:border-accent/40 hover:bg-tint/[0.02] transition-colors flex items-center gap-3 disabled:cursor-default"
                    >
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 min-w-0">
                                {plate(v.vrm)}
                                <span className="text-sm text-fg truncate">
                                    {[v.year, v.make, v.model].filter(Boolean).join(' ') || 'Unknown vehicle'}
                                </span>
                            </div>
                            <p className="text-xs text-fg-4 mt-1 truncate">
                                {v.owner_name ?? 'No owner on file'}
                            </p>
                        </div>
                        {v.owner_id && <ChevronRight className="w-4 h-4 text-fg-4 shrink-0" />}
                    </button>
                )}
            />
        );
    }

    const archivedCount = customers.filter((c) => c.archived_at).length;
    const visibleCustomers = showArchived ? customers : customers.filter((c) => !c.archived_at);

    return (
        <ListScreen<CustomerRow>
            title="All customers"
            subtitle={`${visibleCustomers.length} on file`}
            icon={Users}
            items={visibleCustomers}
            loading={loading}
            keyOf={(c) => c.id}
            searchText={(c) => [c.full_name, c.vrm, c.vehicle_desc].filter(Boolean).join(' ')}
            searchPlaceholder="Search name or registration…"
            emptyTitle="No customers yet"
            emptyBody="Add one from the Clients page, or book a job in and they'll appear here."
            renderItem={(c) => (
                <button
                    onClick={() => navigate(`/clients/${c.id}`)}
                    className="w-full text-left rounded-xl border border-line bg-panel p-4 hover:border-accent/40 hover:bg-tint/[0.02] transition-colors flex items-center gap-3"
                >
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                            <p className={`font-bold truncate ${c.archived_at ? 'text-fg-4' : 'text-fg'}`}>{c.full_name}</p>
                            {c.archived_at && (
                                <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gold-fill/15 text-gold border border-gold/25">
                                    Archived
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {c.vrm ? plate(c.vrm) : <span className="text-xs text-fg-4">No vehicle on file</span>}
                            {c.vehicle_desc && <span className="text-xs text-fg-4 truncate">{c.vehicle_desc}</span>}
                        </div>
                        <p className="text-xs text-fg-4 mt-1">
                            {c.last_seen ? `Last in ${fmtDate(c.last_seen)}` : 'No completed work yet'}
                        </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-fg-4 shrink-0" />
                </button>
            )}
        >
            {/* Only offered once there is something to show. A toggle for a set
                that is always empty is a question the user cannot answer. */}
            {archivedCount > 0 && (
                <button
                    type="button"
                    onClick={() => setShowArchived((v) => !v)}
                    className={`flex items-center gap-2 min-h-[44px] px-3 rounded-lg border text-sm font-bold transition-colors ${
                        showArchived
                            ? 'bg-gold-fill/10 border-gold/30 text-gold'
                            : 'bg-tint/5 border-line text-fg-3 hover:text-fg'
                    }`}
                >
                    <Archive className="w-4 h-4 shrink-0" />
                    {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
                </button>
            )}
        </ListScreen>
    );
}
